import { Injectable } from "@angular/core";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  formatISO,
  getDay,
  isSunday,
  isValid,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfWeek
} from "date-fns";
import { range } from "lodash";

import { Datepicker } from "@lantern/app/models";

export interface Month {
  startDate: string;
  endDate: string;
  weeks: Week[];
}

export type Week = Day[];

export interface Day {
  visible: boolean;
  date?: Date;
  dateIso?: string;
  selected?: boolean;
  firstSelected?: boolean;
  lastSelected?: boolean;
}

@Injectable()
export class DateService {
  get currentDate(): string {
    return this.isoFormatted(Date.now());
  }

  get currentMonth(): Month {
    return this.getMonth(this.currentDate);
  }

  isValidDate(date: string): boolean {
    return isValid(parseISO(date));
  }

  isoFormatted(date: number | Date | string): string {
    return formatISO(typeof date === "string" ? parseISO(date) : date, {
      representation: "date"
    });
  }

  getNDaysFrom(
    numberOfDays: number,
    fromDate: string = this.currentDate
  ): string {
    const date = addDays(parseISO(fromDate), numberOfDays);
    return this.isoFormatted(date);
  }

  getFirstDayOfWeek(fromDate: string = this.currentDate): string {
    const date = startOfWeek(parseISO(fromDate));
    return this.isoFormatted(date);
  }

  getFirstDayOfMonth(fromDate: string = this.currentDate): string {
    const date = startOfMonth(parseISO(fromDate));
    return this.isoFormatted(date);
  }

  getLastDayOfMonth(fromDate: string = this.currentDate): string {
    const date = endOfMonth(parseISO(fromDate));
    return this.isoFormatted(date);
  }

  getPreviousMonth(fromDate: string = this.currentDate): string {
    return this.getAdjacentMonth(-1, fromDate);
  }

  getNextMonth(fromDate: string = this.currentDate): string {
    return this.getAdjacentMonth(+1, fromDate);
  }

  getMonthRange(
    monthsBehind: number,
    monthsAhead: number,
    {
      fromDate = this.currentDate,
      toggleDays
    }: { fromDate?: string; toggleDays?: Datepicker | string } = {}
  ): Month[] {
    const months = range(monthsBehind, monthsAhead + 1).map((offset) =>
      this.getMonth(this.isoFormatted(addMonths(parseISO(fromDate), offset)))
    );

    if (toggleDays != null) {
      this.toggleDays(months, toggleDays);
    }

    return months;
  }

  toggleDays(months: Month[], date: Datepicker | string): void {
    months.forEach((month) => {
      month.weeks.forEach((week) =>
        week.forEach((day) => this.toggleDay(day, date))
      );
    });
  }

  private getAdjacentMonth(offset: number, fromDate: string): string {
    const date = addMonths(parseISO(fromDate), offset);
    return this.isoFormatted(date);
  }

  private getMonth(fromDate: string): Month {
    const startDate = this.getFirstDayOfMonth(fromDate);
    const endDate = this.getLastDayOfMonth(fromDate);
    return {
      startDate,
      endDate,
      weeks: this.appendLeadingAndTrailingWeekdays(
        this.getWeeks(startDate, endDate)
      )
    };
  }

  private getWeeks(startDate: string, endDate: string): Day[][] {
    const days = eachDayOfInterval({
      start: parseISO(startDate),
      end: parseISO(endDate)
    });
    return days.reduce((weeks, date) => {
      if (isSunday(date) || weeks.length === 0) {
        return [...weeks, [this.getDay(date)]];
      } else {
        return [
          ...weeks.slice(0, weeks.length - 1),
          [...weeks[weeks.length - 1], this.getDay(date)]
        ];
      }
    }, [] as Week[]);
  }

  private appendLeadingAndTrailingWeekdays(weeks: Week[]): Week[] {
    return [
      this.withLeadingWeekdays(weeks[0]),
      ...weeks.slice(1, weeks.length - 1),
      this.withTrailingWeekdays(weeks[weeks.length - 1])
    ];
  }

  private getDay(fromDate: Date): Day {
    return {
      date: fromDate,
      dateIso: this.isoFormatted(fromDate),
      visible: true,
      selected: false,
      firstSelected: false,
      lastSelected: false
    };
  }

  private withLeadingWeekdays(week: Day[]): Week {
    const firstDate = week[0].date;
    const firstDayOfWeek = (firstDate && getDay(firstDate)) || 0;
    const leadingDays = range(0, firstDayOfWeek).map(() => ({
      visible: false
    }));
    return [...leadingDays, ...week];
  }

  private withTrailingWeekdays(week: Day[]): Week {
    const lastDate = week[week.length - 1].date;
    const lastDayOffWeek = (lastDate && getDay(lastDate)) || 0;
    const trailingDays = range(lastDayOffWeek, 6).map(() => ({
      visible: false
    }));
    return [...week, ...trailingDays];
  }

  private toggleDay(day: Day, date: Datepicker | string): void {
    if (typeof date === "string") {
      day.selected = day.dateIso === date;
      day.firstSelected = day.dateIso === date;
      day.lastSelected = day.dateIso === date;
    } else {
      day.firstSelected = day.dateIso === date.startDate;
      day.lastSelected = day.dateIso === date.endDate;
      day.selected =
        day.date != null &&
        isWithinInterval(day.date, {
          start: parseISO(date.startDate),
          end: parseISO(date.endDate)
        });
    }
  }
}
